import React, { useRef, useState, useEffect } from 'react';
import * as Yup from 'yup';
import { ScopeCertificateDates } from '../../../sc-models/scope-certificate-details-dto';
import FormBuilder from '../../../../../../shared/components/form-builder/form-builder';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { Utills } from '../../../../../../shared/app-configs/utilit';
import { dtrackItServiceV4 } from '../../../sc-service/dtrackit.service';

interface ScopeCertificateDatesFormProps {
  formRef: any;
  datesData: ScopeCertificateDates;
  setDatesData: React.Dispatch<React.SetStateAction<ScopeCertificateDates>>;
  scStatus?: string; // Add SC Status prop
  isAmendment?: boolean;
}

const ScopeCertificateDatesForm: React.FC<ScopeCertificateDatesFormProps> = ({
  formRef,
  datesData,
  setDatesData,
  scStatus,
  isAmendment,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const [states, setStates] = useState<Array<{value: string, label: string}>>([]);
  const [accreditationBodyOptions, setAccreditationBodyOptions] = useState<Array<{value: string, label: string}>>([]);
  
  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setDatesData(datesData);
  };



  // Get all states from API
  const getStates = async (searchQuery?: string) => {
    try {
      const result: any = await dtrackItServiceV4.getDTrackItRefCodes(
        'ST', // query_param for states
        undefined, // no country parameter
        // 100, // limit
        // 0 // offset
      );
      
      if (result?.data && Array.isArray(result.data)) {
        let filteredStates = result.data;
        
        // Filter by search query if provided
        if (searchQuery) {
          filteredStates = result.data.filter((state: any) => 
            state.description_1.toLowerCase().includes(searchQuery.toLowerCase()) ||
            state.code_1.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }
        
        const states = filteredStates.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setStates(states);
      } else {
        setStates([]);
      }
    } catch (err) {
      Utills.cl(`Error in Fetching States ${err}`);
      setStates([]);
    }
  };

  // Get accreditation body options from API
  const getAccreditationBody = async () => {
    try {
      const result: any = await dtrackItServiceV4.getAccreditationBody();
      
      if (result?.data && Array.isArray(result.data)) {
        const accreditationBodies = result.data.map((item: any) => ({
          value: item.code_1,
          label: item.description_1,
        }));
        setAccreditationBodyOptions(accreditationBodies);
      } else {
        setAccreditationBodyOptions([]);
      }
    } catch (err) {
      Utills.cl(`Error in Fetching Accreditation Body ${err}`);
      setAccreditationBodyOptions([]);
    }
  };

  useEffect(() => {
    getStates();
    getAccreditationBody();
  }, []);

  // Update form when datesData changes
  useEffect(() => {
    if (formRef?.current) {
      formRef.current.setValues(datesData);
      
      // Auto-update scValidFromDate based on business logic
      if (datesData.scCreatedDate) {
        const currentValidFromDate = datesData.scValidFromDate;
        
        // Auto-update when scValidFromDate is empty OR when created date is after current valid from date
        if (!currentValidFromDate || new Date(datesData.scCreatedDate) > new Date(currentValidFromDate)) {
          formRef.current.setFieldValue('scValidFromDate', datesData.scCreatedDate);
        }
      }
    }
  }, [datesData, formRef]);



  // Commented out hardcoded options - now using API calls
  // const accreditationBodyOptions = [
  //   { label: "SLAB", value: "SLAB" },
  //   { label: "IOAS", value: "IOAS" },
  //   { label: "ANAB", value: "ANAB" },
  //   { label: "TAF", value: "TAF" },
  //   { label: "COFRAC", value: "COFRAC" },
  //   { label: "IA Japan", value: "IAJapan" },
  //   { label: "DAkkS", value: "DAkkS" },
  // ];

  const formConfig = {
    pageName: "scope-certificate-details",
    initialValues: datesData,
    validationSchema: Yup.object().shape({
      scCreatedDate: Yup.date()
        .required('SC First Created Date is required'),
      
      scLastUpdatedDate: Yup.date()
        .required('SC Last Updated Date is required')
        .test('last-updated-sequence', 'Last Updated Date must be after or equal to First Created Date',
          function(value) {
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated || !value) return true;
            return new Date(firstCreated) <= new Date(value);
          })
        .test('last-updated-amendment', 'During amendments, Last Updated Date must be after or equal to Created Date',
          function(value) {
            if (!isAmendment || !value) return true;
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated) return true;
            return new Date(firstCreated) <= new Date(value);
          }),
      
      scExtendedUntilDate: Yup.date()
        .nullable()
        .test('extended-until-sequence', 'Extended Until Date must be after or equal to Valid Until Date',
          function(value) {
            const validUntil = this.parent.scValidFromDate;
            if (!validUntil || !value) return true;
            return new Date(validUntil) <= new Date(value);
          }),
      
      scLastAuditStartDate: Yup.date()
        .nullable(),
      
      scValidFromDate: Yup.date()
        .required('SC Valid From Date is required')
        .test('valid-from-sequence', 'Valid From Date must be after or equal to First Created Date',
          function(value) {
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated || !value) return true;
            return new Date(firstCreated) <= new Date(value);
          })
        .test('valid-from-max-days', 'SC Valid From Date cannot be more than 60 days after SC Created Date',
          function(value) {
            const firstCreated = this.parent.scCreatedDate;
            if (!firstCreated || !value) return true;
            const createdDate = new Date(firstCreated);
            const validFromDate = new Date(value);
            const daysDifference = Math.ceil((validFromDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
            return daysDifference <= 60;
          }),
      
      scValidUntilDate: Yup.date()
        .required('SC Valid Until Date is required')
        .test('valid-until-sequence', 'Valid Until Date must be after or equal to Valid From Date',
          function(value) {
            const validFrom = this.parent.scValidFromDate;
            if (!validFrom || !value) return true;
            return new Date(validFrom) <= new Date(value);
          }),
      
      scWithdrawalDate: Yup.date()
        .when('scStatus', {
          is: 'W',
          then: (schema) => schema.required('Withdrawal Date is required when SC Status is Withdrawn'),
          otherwise: (schema) => schema.nullable()
        })
        .test('withdrawal-sequence', 'Withdrawal Date must be after or equal to Valid From Date',
          function(value) {
            const validFrom = this.parent.scValidFromDate;
            if (!validFrom || !value) return true;
            return new Date(validFrom) <= new Date(value);
          }),
      
      scLastAuditDate: Yup.date()
        .nullable()
        .test('audit-date-sequence', 'Last Audit Date must be after or equal to Last Audit Start Date', 
          function(value) {
            const startDate = this.parent.scLastAuditStartDate;
            if (!startDate || !value) return true;
            return new Date(startDate) <= new Date(value);
          }),
      
      scStateOrProvince: Yup.string()
        .required('Select State or Province is required'),
      
      scCbAccreditationBody: Yup.string()
        .required('SC CB Accreditation Body is required'),
      
      scCbAccreditationBodyDescription: Yup.string(),
      
      scCbScopeStipulations: Yup.string(),
    }),
    readOnly: hasSubmitSC,
    sections: [
      {
        title: 'Certificate Dates Details',
        fields: [
          {
            label: 'SC Created Date',
            name: 'scCreatedDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: isAmendment, // Disable during amendments
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                if (newValue && formRef?.current) {
                  const currentValidFromDate = updatedValues?.scValidFromDate;
                  
                  // Auto-update scValidFromDate only when it's empty OR when new created date is after current valid from date
                  if (!currentValidFromDate || new Date(newValue) > new Date(currentValidFromDate)) {
                    formRef.current.setFieldValue('scValidFromDate', newValue);
                  }
                }
              },
            },
            props: { 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years")
            },
          },
          {
            label: 'SC Last Updated Date',
            name: 'scLastUpdatedDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scCreatedDate ? moment(values.scCreatedDate) : undefined
            }),
          },
          {
            label: 'SC Extended Until Date',
            name: 'scExtendedUntilDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scValidUntilDate ? moment(values.scValidUntilDate) : undefined
            }),
          },
          {
            label: 'SC Last Audit Start Date',
            name: 'scLastAuditStartDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: { 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years")
            },
          },
          {
            label: 'SC Valid From Date',
            name: 'scValidFromDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: isAmendment, // Disable during amendments
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scCreatedDate ? moment(values.scCreatedDate) : undefined
            }),
          },
          {
            label: 'SC Valid Until Date',
            name: 'scValidUntilDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: true,
            disabled: isAmendment, // Disable during amendments
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scValidFromDate ? moment(values.scValidFromDate) : undefined
            }),
          },
          {
            label: 'SC Withdrawal Date',
            name: 'scWithdrawalDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            // disabled: (values: any) => scStatus === "V",
            visible: (values: any) => scStatus === "W",
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scValidFromDate ? moment(values.scValidFromDate) : undefined
            }),
          },
          {
            label: 'SC Last Audit Date',
            name: 'scLastAuditDate',
            type: 'date',
            gridSize: { xs: 12, md: 6, lg: 3 },
            required: false,
            props: (values: any) => ({ 
              placeholder: 'YYYY-MM-DD',
              maxDate: moment().add(100, "years"),
              minDate: values?.scLastAuditStartDate ? moment(values.scLastAuditStartDate) : undefined
            }),
          },
        ],
      },
      {
        title: 'SC Place of Issue',
        fields: [
          {
            label: 'Select State or Province',
            name: 'scStateOrProvince',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                // Store the description for the selected state
                const selectedState = states.find(state => state.value === newValue);
                if (selectedState) {
                  formRef.current?.setFieldValue('scStateOrProvinceDescription', selectedState.label);
                }
              },
              onSearch: (searchQuery: string) => {
                getStates(searchQuery);
              },
            },
            props: { 
              enableSearch: true,
              options: states,
              placeholder: 'Select State or Province'
            },
          },
        ],
      },
      {
        title: 'Accreditation Details',
        fields: [
          {
            label: 'SC CB Accreditation Body',
            name: 'scCbAccreditationBody',
            type: 'select',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                // Store the description for the selected accreditation body
                const selectedAccreditationBody = accreditationBodyOptions.find(body => body.value === newValue);
                if (selectedAccreditationBody) {
                  formRef.current?.setFieldValue('scCbAccreditationBodyDescription', selectedAccreditationBody.label);
                }
              },
            },
            props: { 
              options: accreditationBodyOptions,
              placeholder: 'Select SC CB Accreditation Body'
            },
          },
          {
            label: 'SC CB Scope Stipulations',
            name: 'scCbScopeStipulations',
            type: 'text',
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            props: { 
              placeholder: 'Enter SC CB Scope Stipulations'
            },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      // Format place of issue as StateName (CountryCode-StateCode)
      const selectedState = states.find(state => state.value === values.scStateOrProvince);
      const placeOfIssue = selectedState ? selectedState.label : '';
      
      const updatedValues = {
        ...values,
        scPlaceOfIssue: placeOfIssue
      };
      
      setDatesData(updatedValues);
      toggleDrawer();
    },
  };

  return <FormBuilder 
    formConfig={formConfig} 
    onSubmit={formConfig.handleSubmit} 
    formRef={formRef} 
  />;
};

export default ScopeCertificateDatesForm;

